import mulligan_bg from "assets/img/mulligan-bg.png";
import {HOCModal} from "components/HOCModal";
import {HOCModalCenterDesktopPosition} from "components/HOCModalCenterDesktopPosition";
import {IconMulligan} from "components/Icons/IconMulliigan";
import {hideMulliganModal} from "modules/actions";
import {isOpenMulliganModal} from "modules/selectors/streak";
import {useAdobeDTM, useIsMobile} from "modules/utils/hooks";
import React, {Fragment, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";
import {getRulesLinkAccordingState} from "modules/utils/dom_helpers";
import {getUserStore} from "modules/selectors";

const MulliganPageHeader = styled.div`
	background: url(${mulligan_bg});
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	height: 350px;
	width: 100%;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #58a3ff;

	&:before,
	&:after {
		content: "";
		display: block;
		height: 100px;
		width: 100%;
		position: absolute;
		left: 0;
		top: 0;
		background: linear-gradient(to bottom, #090b0f 0%, rgba(9, 11, 15, 0) 100%);
	}

	&:after {
		height: 300px;
		top: auto;
		bottom: 0;
		background: linear-gradient(to top, #090b0f 0%, rgba(9, 11, 15, 0) 100%);
	}

	svg {
		width: 200px;
		height: 200px;
		position: relative;
		z-index: 3;
		box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
	}
`;

const Add = styled.button`
	height: 50px;
	width: calc(100% - 50px);
	max-width: 320px;
	background: #142845;
	color: #68a3f8;

	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	line-height: 1;
	border-radius: 60px;
	box-shadow: none;
	border: none;
	margin: 15px auto 35px;
	font-weight: bold;
`;

const ScrollList = styled.div`
	overflow: auto;
`;

const Description = styled.div`
	font-size: 24px;
	line-height: 28px;
	text-align: center;
	font-weight: bold;
`;

const DescriptionCopy = styled.div`
	font-size: 10px;
	line-height: 12px;
	text-align: center;
	color: #fff;
	max-width: 280px;
	margin: 10px auto 50px;
	font-weight: normal;

	a {
		color: #2687ff;
		font-weight: bold;
	}
`;

const PAGE_NAME = "mlb play : beat the streak : mulligan";

const MulliganPage: React.FC = () => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const isOpen = useSelector(isOpenMulliganModal);
	const isMobile = useIsMobile();
	const {countryCode, stateCode} = useSelector(getUserStore);

	const {trackAction, trackState} = useAdobeDTM();

	useEffect(() => {
		if (isOpen) {
			trackState({
				"page.name": PAGE_NAME,
				"page.subType": "mulligan",
			});
		}
	}, [isOpen, trackState]);

	const onToggle = () => {
		trackAction({
			"page.name": PAGE_NAME,
			"action.name": "mlb play : beat the streak : mulligian : exit click",
		});
		dispatch(hideMulliganModal());
	};

	const rulesClick = () => {
		trackAction({
			"page.name": PAGE_NAME,
			"action.name": "mlb play : beat the streak : mulligian : official rules click",
			"action.element.text": "official rules",
		});
	};

	const Content = () => (
		<ScrollList>
			<MulliganPageHeader>
				<IconMulligan />
			</MulliganPageHeader>

			<Description>
				<h4>{i18n.t("mulligan.header.title", "Mulligan")}</h4>

				<DescriptionCopy>
					{i18n.t("mulligan.description.part_one")}{" "}
					<a
						href={getRulesLinkAccordingState(countryCode, stateCode)}
						onClick={rulesClick}
					>
						{i18n.t("home_page.header.purchase_rules_link")}
					</a>
				</DescriptionCopy>
			</Description>
			<Add onClick={onToggle}>{i18n.t("mulligan.button.done", "Done")}</Add>
		</ScrollList>
	);

	const props = {
		isOpen,
		onClose: onToggle,
		name: "mulligan",
		headerText: (
			<Fragment>
				{i18n.t("mulligan.header.title", "Mulligan")},{" "}
				{i18n.t("home_page.header.purchase_rules_link")}
			</Fragment>
		),
	};

	return isMobile ? (
		<HOCModal {...props}>
			<Content />
		</HOCModal>
	) : (
		<HOCModalCenterDesktopPosition {...props}>
			<Content />
		</HOCModalCenterDesktopPosition>
	);
};

export default MulliganPage;
